// Web Bluetooth API for browser-based Bluetooth thermal printer support
// This uses the Web Bluetooth API (navigator.bluetooth) for web platforms
// Reference: https://developer.mozilla.org/en-US/docs/Web/API/Web_Bluetooth_API

// Thermal printer service UUID (common for ESC/POS printers)
const PRINTER_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';
const PRINTER_CHARACTERISTIC_UUID = '00002af1-0000-1000-8000-00805f9b34fb';

// Alternative UUIDs for different printer types
const ALTERNATIVE_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Standard thermal printer service
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Some Chinese thermal printers
  '0000ff00-0000-1000-8000-00805f9b34fb', // Generic UART service
];

const ALTERNATIVE_CHARACTERISTIC_UUIDS = [
  '00002af1-0000-1000-8000-00805f9b34fb', // Standard write characteristic
  '49535343-8841-43f4-a8d4-ecbe34729bb3', // Some Chinese thermal printers
  '0000ff02-0000-1000-8000-00805f9b34fb', // Generic write characteristic
];

interface BluetoothDeviceInfo {
  id: string;
  name: string;
  address?: string;
  isPaired?: boolean;
}

class WebBluetoothPrinterService {
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private server: BluetoothRemoteGATTServer | null = null;

  /**
   * Check if Web Bluetooth API is supported in the current browser
   */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
  }

  /**
   * Check if the site is running on HTTPS (required for Web Bluetooth)
   */
  isSecureContext(): boolean {
    return typeof window !== 'undefined' && window.isSecureContext;
  }

  /**
   * Check if a device is currently connected
   */
  isConnected(): boolean {
    return this.device !== null && 
           this.device.gatt !== undefined && 
           this.device.gatt.connected;
  }

  /**
   * Request user to select a Bluetooth device
   * This will show the browser's native Bluetooth device picker
   */
  async requestDevice(): Promise<BluetoothDeviceInfo> {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth API is not supported in this browser. Please use Chrome, Edge, or Opera.');
    }

    // Check if running on HTTPS (required for Web Bluetooth)
    if (!this.isSecureContext()) {
      throw new Error('Web Bluetooth requires HTTPS. Please access this site using https:// instead of http://. Contact your administrator to enable HTTPS.');
    }

    try {
      // Request device - accept all devices to ensure compatibility with all printers
      // Use optionalServices to allow access to common printer services
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true, // Show all Bluetooth devices
        optionalServices: [
          ...ALTERNATIVE_SERVICE_UUIDS,
          ...ALTERNATIVE_CHARACTERISTIC_UUIDS,
          // Add generic Serial Port Profile UUID
          '00001101-0000-1000-8000-00805f9b34fb',
        ],
      });

      console.log('Bluetooth device selected:', device.name || device.id);
      
      return {
        id: device.id,
        name: device.name || 'Unknown Printer',
        address: device.id,
        isPaired: true,
      };
    } catch (error: any) {
      console.error('Error requesting Bluetooth device:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      if (error.name === 'NotFoundError') {
        throw new Error('No Bluetooth device selected. Please select a device from the list and try again.');
      } else if (error.name === 'SecurityError') {
        throw new Error('Bluetooth permission denied. Please allow Bluetooth access when prompted by your browser.');
      } else if (error.name === 'NotAllowedError') {
        throw new Error('Bluetooth permission was denied or cancelled. Please grant permission when prompted by your browser.');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('Bluetooth is not available. Please enable Bluetooth on your device and try again.');
      }
      throw new Error(`Failed to request Bluetooth device: ${error.message || 'Unknown error. Please ensure Bluetooth is enabled and you grant permission when prompted.'}`);
    }
  }

  /**
   * Connect to a Bluetooth device
   */
  async connect(deviceId?: string): Promise<boolean> {
    try {
      // If no deviceId provided or we don't have a cached device, request a new one
      if (!deviceId || !this.device) {
        const deviceInfo = await this.requestDevice();
        // The device is automatically stored in this.device during requestDevice
        this.device = await this.getDeviceById(deviceInfo.id);
      }

      if (!this.device) {
        throw new Error('No Bluetooth device available to connect.');
      }

      // Connect to GATT server
      console.log('Connecting to GATT Server...');
      this.server = await this.device.gatt!.connect();
      console.log('Connected to GATT Server');

      // Try to find the printer service and characteristic
      let serviceFound = false;
      let characteristicFound = false;

      // Try each alternative service UUID
      for (const serviceUuid of ALTERNATIVE_SERVICE_UUIDS) {
        try {
          console.log(`Trying service UUID: ${serviceUuid}`);
          const service = await this.server.getPrimaryService(serviceUuid);
          console.log('Service found:', service);
          serviceFound = true;

          // Try to find the characteristic
          for (const charUuid of ALTERNATIVE_CHARACTERISTIC_UUIDS) {
            try {
              console.log(`Trying characteristic UUID: ${charUuid}`);
              this.characteristic = await service.getCharacteristic(charUuid);
              console.log('Characteristic found:', this.characteristic);
              characteristicFound = true;
              break;
            } catch (charError) {
              console.warn(`Characteristic ${charUuid} not found, trying next...`);
            }
          }

          if (characteristicFound) break;
        } catch (serviceError) {
          console.warn(`Service ${serviceUuid} not found, trying next...`);
        }
      }

      if (!serviceFound) {
        throw new Error('Printer service not found. This device may not be a compatible thermal printer.');
      }

      if (!characteristicFound) {
        throw new Error('Printer write characteristic not found. This device may not support printing.');
      }

      console.log('Successfully connected to Bluetooth printer');
      return true;
    } catch (error: any) {
      console.error('Error connecting to Bluetooth device:', error);
      this.device = null;
      this.characteristic = null;
      this.server = null;
      throw new Error(`Failed to connect to printer: ${error.message || error}`);
    }
  }

  /**
   * Helper method to get device by ID from previously paired devices
   */
  private async getDeviceById(deviceId: string): Promise<BluetoothDevice | null> {
    try {
      // Web Bluetooth doesn't provide a way to get devices by ID
      // We need to request the device again
      // For now, return the currently stored device
      return this.device;
    } catch (error) {
      console.error('Error getting device by ID:', error);
      return null;
    }
  }

  /**
   * Disconnect from the current Bluetooth device
   */
  async disconnect(): Promise<void> {
    try {
      if (this.device && this.device.gatt && this.device.gatt.connected) {
        await this.device.gatt.disconnect();
        console.log('Disconnected from Bluetooth device');
      }
      this.device = null;
      this.characteristic = null;
      this.server = null;
    } catch (error: any) {
      console.error('Error disconnecting from Bluetooth device:', error);
      throw new Error(`Failed to disconnect: ${error.message || error}`);
    }
  }

  /**
   * Send data to the connected Bluetooth printer
   * @param data - String or binary (Uint8Array) data to send
   */
  async write(data: string | Uint8Array): Promise<boolean> {
    if (!this.isConnected() || !this.characteristic) {
      throw new Error('Bluetooth printer not connected. Please connect first.');
    }

    try {
      const bytes = typeof data === 'string'
        ? new TextEncoder().encode(data)
        : data;

      // Split data into chunks if necessary (some printers have MTU limits)
      const chunkSize = 512; // Safe chunk size for most printers
      const chunks = [];
      
      for (let i = 0; i < bytes.length; i += chunkSize) {
        chunks.push(bytes.slice(i, i + chunkSize));
      }

      console.log(`Sending ${chunks.length} chunks to printer...`);

      // Send each chunk
      for (let i = 0; i < chunks.length; i++) {
        await this.characteristic.writeValue(chunks[i]);
        console.log(`Chunk ${i + 1}/${chunks.length} sent`);
        
        // Small delay between chunks to prevent buffer overflow
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      console.log('Successfully sent data to Bluetooth printer');
      return true;
    } catch (error: any) {
      console.error('Error sending data to Bluetooth printer:', error);
      throw new Error(`Failed to send data: ${error.message || error}`);
    }
  }

  /**
   * Get the currently connected device info
   */
  getConnectedDevice(): BluetoothDeviceInfo | null {
    if (!this.device) return null;

    return {
      id: this.device.id,
      name: this.device.name || 'Unknown Printer',
      address: this.device.id,
      isPaired: true,
    };
  }

  /**
   * Get list of previously paired devices
   * Note: Web Bluetooth API doesn't provide access to previously paired devices
   * Users must re-select devices each time
   */
  async listDevices(): Promise<BluetoothDeviceInfo[]> {
    console.warn('Web Bluetooth API does not support listing previously paired devices.');
    console.warn('Users must use the requestDevice() method to select a device each time.');
    
    // Return currently connected device if available
    const connected = this.getConnectedDevice();
    return connected ? [connected] : [];
  }
}

// Export singleton instance
export const webBluetoothPrinter = new WebBluetoothPrinterService();

// Export utility functions for compatibility with existing code
export async function isWebBluetoothSupported(): Promise<boolean> {
  return webBluetoothPrinter.isSupported();
}

export async function requestWebBluetoothDevice(): Promise<BluetoothDeviceInfo> {
  return await webBluetoothPrinter.requestDevice();
}

export async function connectWebBluetoothDevice(deviceId?: string): Promise<boolean> {
  return await webBluetoothPrinter.connect(deviceId);
}

export async function disconnectWebBluetoothDevice(): Promise<void> {
  return await webBluetoothPrinter.disconnect();
}

export async function sendWebBluetoothData(data: string | Uint8Array): Promise<boolean> {
  return await webBluetoothPrinter.write(data);
}

export async function checkWebBluetoothConnection(): Promise<boolean> {
  return webBluetoothPrinter.isConnected();
}

export async function getWebBluetoothDevices(): Promise<BluetoothDeviceInfo[]> {
  return await webBluetoothPrinter.listDevices();
}

